import React, { useEffect, useMemo, useRef, useState } from "react";
import { Maximize2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Mascot } from "../components/Mascot";
import { GaseousOverlay } from "../components/GaseousOverlay";
import { gaseousLesson, gaseousStoryboard } from "../lib/gaseousLesson";
import {
    loadStoredStoryboard,
    saveStoredStoryboard,
} from "../lib/storyboard";
import { useSessionChannel } from "../hooks/useSessionChannel";
import JoinQR from "../components/JoinQR";
import StudentAnswerPhotoOverlay from "../components/StudentAnswerPhotoOverlay";

const requestFullscreen = async (el) => {
    const target = el || document.documentElement;
    const fn =
        target.requestFullscreen ||
        target.webkitRequestFullscreen ||
        target.mozRequestFullScreen ||
        target.msRequestFullscreen;
    if (!fn) return false;
    try {
        await fn.call(target);
        return true;
    } catch {
        return false;
    }
};

const isFullscreen = () =>
    !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
    );

class PCMQueuePlayer {
    constructor(sampleRate = 24000) {
        this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        this.sampleRate = sampleRate;
        this.nextStartTime = this.audioCtx.currentTime;
        this.activeSources = [];
        this.onEnded = null;
        this.checkEndedTimer = null;
    }

    playChunk(base64Data) {
        try {
            if (this.audioCtx.state === "suspended") {
                this.audioCtx.resume();
            }
            const raw = atob(base64Data);
            const bytes = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) {
                bytes[i] = raw.charCodeAt(i);
            }
            const int16 = new Int16Array(bytes.buffer);
            
            const float32 = new Float32Array(int16.length);
            for (let i = 0; i < int16.length; i++) {
                float32[i] = int16[i] / 32768.0;
            }

            const buffer = this.audioCtx.createBuffer(1, float32.length, this.sampleRate);
            buffer.copyToChannel(float32, 0);

            const source = this.audioCtx.createBufferSource();
            source.buffer = buffer;
            source.connect(this.audioCtx.destination);

            const startTime = Math.max(this.audioCtx.currentTime, this.nextStartTime);
            source.start(startTime);
            this.activeSources.push(source);
            this.nextStartTime = startTime + buffer.duration;

            if (this.checkEndedTimer) clearTimeout(this.checkEndedTimer);
            const remainingTimeMs = (this.nextStartTime - this.audioCtx.currentTime) * 1000;
            this.checkEndedTimer = setTimeout(() => {
                if (this.onEnded) this.onEnded();
            }, remainingTimeMs + 200);

        } catch (e) {
            console.error("Error playing audio chunk", e);
        }
    }

    stop() {
        if (this.checkEndedTimer) clearTimeout(this.checkEndedTimer);
        this.activeSources.forEach(source => {
            try {
                source.stop();
            } catch (e) {}
        });
        this.activeSources = [];
        try {
            this.audioCtx.close();
        } catch (e) {}
    }
}

const GaseousStudentDisplay = () => {
    const sessionId = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("sessionId") || "gaseous-demo";
    }, []);
 
    const [deckState, setDeckState] = useState({
        slide_no: 1,
        fragment_no: 0,
        total_fragments: 0,
    });
    const [popupOpen, setPopupOpen] = useState(false);
    const [overlay, setOverlay] = useState(null);
    const [studentAnswerPhoto, setStudentAnswerPhoto] = useState(null);
    const [showFsButton, setShowFsButton] = useState(false);
    const pcmPlayerRef = useRef(null);
    const [aiOverlayState, setAiOverlayState] = useState(null);

    /* ---- Seed the pre-authored storyboard if not present (mirrors
       what TeacherPanelDeck does) — necessary if a teacher opens the
       student URL directly without going through the landing page. */
    useEffect(() => {
        if (!loadStoredStoryboard("gaseous-state")) {
            saveStoredStoryboard("gaseous-state", gaseousStoryboard);
        }
    }, []);

    /* ---- session channel: BroadcastChannel + WebSocket ---- */
    const channel = useSessionChannel(sessionId);
    useEffect(() => {
        if (!channel?.onMessage) return;
        return channel.onMessage((m) => {
            if (m.role === "student-shell") return;
            if (m.type === "DECK_STATE") {
                setDeckState({
                    slide_no: m.slide_no || 1,
                    fragment_no: Math.max(0, m.fragment_no || 0),
                    total_fragments: m.total_fragments || 0,
                });
            } else if (m.type === "DECK_POPUP") {
                setPopupOpen(true);
            } else if (m.type === "DECK_POPUP_CLEARED") {
                setPopupOpen(false);
            } else if (m.type === "DECK_OVERLAY") {
                setOverlay(m.overlay || null);
            } else if (m.type === "STATE_SYNC" && m.payload) {
                if ("studentAnswerPhoto" in m.payload) {
                    setStudentAnswerPhoto(m.payload.studentAnswerPhoto);
                }
            } else if (m.type === "AI_SESSION_START") {
                if (channel?.send) {
                    channel.send({ type: "DECK_PAUSE_AUDIO" });
                }

                if (pcmPlayerRef.current) {
                    pcmPlayerRef.current.stop();
                }

                setAiOverlayState("idle");
            } else if (m.type === "AI_AUDIO_LISTEN") {
                setAiOverlayState("listening");
            } else if (m.type === "AI_AUDIO_THINK") {
                setAiOverlayState("thinking");
            } else if (m.type === "AI_AUDIO_START") {
                setAiOverlayState("speaking");

                const player = new PCMQueuePlayer(24000);
                player.onEnded = () => {
                    setAiOverlayState(null);
                    if (channel?.send) {
                        channel.send({ type: "DECK_PLAY_AUDIO" });
                    }
                };
                pcmPlayerRef.current = player;
            } else if (m.type === "AI_AUDIO_CHUNK") {
                if (pcmPlayerRef.current) {
                    pcmPlayerRef.current.playChunk(m.payload);
                }
            } else if (m.type === "AI_AUDIO_STOP") {
                if (pcmPlayerRef.current) {
                    pcmPlayerRef.current.stop();
                    pcmPlayerRef.current = null;
                }
                setAiOverlayState(null);
                if (channel?.send) {
                    channel.send({ type: "DECK_PLAY_AUDIO" });
                }
            }
        });
    }, [channel]);

    /* ---- auto-fullscreen on first launch (Classroom Mode) ---- */
    useEffect(() => {
        let cancelled = false;
        const tryFs = async () => {
            if (isFullscreen()) return;
            const ok = await requestFullscreen();
            if (cancelled) return;
            if (!ok) setShowFsButton(true);
        };
        tryFs();

        const onMsg = (e) => {
            if (e?.data?.type === "sparkl:fullscreen") tryFs();
        };
        window.addEventListener("message", onMsg);

        const onFsChange = () => {
            if (isFullscreen()) setShowFsButton(false);
        };
        document.addEventListener("fullscreenchange", onFsChange);
        document.addEventListener("webkitfullscreenchange", onFsChange);
        return () => {
            cancelled = true;
            window.removeEventListener("message", onMsg);
            document.removeEventListener("fullscreenchange", onFsChange);
            document.removeEventListener("webkitfullscreenchange", onFsChange);
        };
    }, []);

    /* ---- derive caption + mascot state ---- */
    const storyboard = useMemo(
        () => loadStoredStoryboard("gaseous-state") || gaseousStoryboard,
        [],
    );
    const currentEntry = useMemo(() => {
        if (!storyboard?.length) return null;
        return (
            storyboard.find(
                (e) =>
                    (e.type === "say" ||
                        e.type === "concept_check" ||
                        e.type === "engagement_check") &&
                    e.slide_no === deckState.slide_no &&
                    (e.fragment_no ?? 0) === deckState.fragment_no,
            ) || null
        );
    }, [storyboard, deckState.slide_no, deckState.fragment_no]);

    const caption =
        currentEntry?.text ||
        currentEntry?.question ||
        currentEntry?.narration ||
        "";

    let mascotState = "speaking";
    if (popupOpen) mascotState = "thinking";
    else if (!caption) mascotState = "idle";

    const iframeSrc = `${gaseousLesson.deckUrl}?role=student&sessionId=${encodeURIComponent(
        sessionId,
    )}`;

    return (
        <div
            className="w-screen h-screen overflow-hidden ambient-bg grain relative"
            data-testid="gaseous-student-display"
        >
            {/* Centered 16:9 deck — 80% of viewport width, capped to height */}
            <div className="absolute inset-0 flex items-center justify-center px-12">
                <div
                    className="relative rounded-3xl overflow-hidden bg-black soft-shadow"
                    style={{
                        width: "80vw",
                        maxWidth: "min(80vw, calc((100vh - 96px) * 16 / 9))",
                        aspectRatio: "16 / 9",
                        boxShadow:
                            "0 30px 80px rgba(20, 32, 42, 0.18), 0 2px 0 rgba(255,255,255,0.4) inset",
                    }}
                    data-testid="gaseous-deck-frame"
                >
                    <iframe
                        src={iframeSrc}
                        title="Gaseous state"
                        className="w-full h-full block"
                        sandbox="allow-scripts allow-same-origin allow-presentation allow-forms"
                    />
                </div>
            </div>

            {/* Floating AI tutor mascot — bottom-right, same vibe as photosynthesis */}
            {!aiOverlayState && (
                <div
                    className="fixed bottom-10 right-12 z-30 pointer-events-none"
                    data-testid="gaseous-student-mascot"
                >
                    <div className="flex flex-col items-end gap-3">
                        {caption && (
                            <div
                                className="max-w-[420px] glass-card px-5 py-3 rounded-2xl pointer-events-auto"
                                data-testid="gaseous-student-caption"
                            >
                                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-1 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full sparkl-gradient animate-pulse" />
                                    AI tutor
                                </p>
                                <p className="text-[14px] leading-snug text-[#14202a]">
                                    {caption}
                                </p>
                            </div>
                        )}
                        <Mascot state={mascotState} size="sm" />
                    </div>
                </div>
            )}

            {/* Slide pill — top-left */}
            <div
                className="fixed top-6 left-8 z-30 glass-card px-3.5 py-1.5 rounded-full flex items-center gap-2"
                data-testid="gaseous-student-slide-pill"
            >
                <span className="w-1.5 h-1.5 rounded-full sparkl-gradient" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold">
                    {gaseousLesson.sessionName}
                </span>
                <span className="text-[10px] text-[#4a5662]">·</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#14202a] font-semibold">
                    Slide {deckState.slide_no}/{gaseousLesson.slides.length}
                </span>
            </div>

            {showFsButton && (
                <button
                    data-testid="gaseous-student-fullscreen-btn"
                    onClick={async () => {
                        const ok = await requestFullscreen();
                        if (ok) setShowFsButton(false);
                    }}
                    className="fixed top-4 right-4 z-50 px-3 py-2 rounded-full bg-white/90 backdrop-blur border border-black/[0.06] shadow-[0_8px_24px_rgba(20,32,42,0.12)] hover:bg-white text-xs font-semibold text-[#14202a] flex items-center gap-1.5 transition-all active:scale-[0.97]"
                    title="Enter fullscreen"
                >
                    <Maximize2 className="w-3.5 h-3.5 text-[#2E8E9A]" />
                    Enter fullscreen
                </button>
            )}

            {/* Teacher-driven overlays (real-life examples, MCQ, subjective,
                drag-drop activity, recap) — render OVER the deck so the class
                sees the same surface the teacher is presenting. */}
            <GaseousOverlay overlay={overlay} />

            {/* Pairing QR for the Sparkl Teacher mobile app */}
            {!aiOverlayState && <JoinQR sessionId={sessionId} compact={!!overlay} />}

            {/* Student-answer photo overlay from the Camera FAB */}
            <StudentAnswerPhotoOverlay
                photo={studentAnswerPhoto}
                onClose={() => setStudentAnswerPhoto(null)}
            />

            {/* ── AI Speaking Overlay ──────────────────────────────────────
                 When the teacher activates the AI voice assistant, we blur
                 the entire student display and bring the Mascot large at
                 the center. This signals to students that Sparkl is speaking.
                 Nothing on the lesson content can be interacted with during
                 this time. The overlay fades + scales out smoothly when AI is done. */}
            <AnimatePresence>
                {aiOverlayState && (
                    <motion.div
                        key="ai-speaking-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-0 z-[200] flex flex-col items-center justify-center"
                        style={{
                            backdropFilter: "blur(18px) saturate(140%)",
                            WebkitBackdropFilter: "blur(18px) saturate(140%)",
                            backgroundColor: "rgba(10, 16, 22, 0.55)",
                            pointerEvents: "all",
                        }}
                        data-testid="ai-speaking-overlay"
                    >
                        {/* Large centered Mascot */}
                        <motion.div
                            initial={{ scale: 0.45, opacity: 0, y: 60 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.5, opacity: 0, y: 40 }}
                            transition={{
                                duration: 1.1,
                                ease: [0.22, 1, 0.36, 1],
                                opacity: { duration: 0.6 },
                            }}
                            style={{ willChange: "transform" }}
                        >
                            <Mascot state={aiOverlayState} size="lg" />
                        </motion.div>

                        {/* Subtitle label beneath the mascot */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
                            className="mt-8 text-center"
                        >
                            <p
                                className="text-white/90 text-2xl font-semibold tracking-tight animate-pulse"
                                style={{
                                    textShadow: aiOverlayState === "listening"
                                        ? "0 2px 16px rgba(255,154,120,0.5)"
                                        : "0 2px 16px rgba(91,194,204,0.4)"
                                }}
                            >
                                {aiOverlayState === "idle" && "Sparkl is ready"}
                                {aiOverlayState === "listening" && "Sparkl is listening"}
                                {aiOverlayState === "thinking" && "Sparkl is thinking..."}
                                {aiOverlayState === "speaking" && "Sparkl is speaking"}
                            </p>
                            <p className="text-white/50 text-sm mt-2 tracking-wide">
                                {aiOverlayState === "idle" && "Your teacher is about to ask a question"}
                                {aiOverlayState === "listening" && "Shh... the teacher is asking a question"}
                                {aiOverlayState === "thinking" && "Formulating the best answer..."}
                                {aiOverlayState === "speaking" && "Listen carefully — your AI tutor is answering a question"}
                            </p>
                        </motion.div>

                        {/* Subtle ambient glow rings behind the mascot */}
                        <motion.div
                            className="absolute rounded-full pointer-events-none"
                            style={{
                                width: 520,
                                height: 520,
                                background: aiOverlayState === "listening"
                                    ? "radial-gradient(circle, rgba(255,154,120,0.12) 0%, rgba(255,154,120,0) 70%)"
                                    : "radial-gradient(circle, rgba(91,194,204,0.12) 0%, rgba(91,194,204,0) 70%)",
                                filter: "blur(30px)",
                            }}
                            animate={{ scale: [1, 1.08, 1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default GaseousStudentDisplay;
